<template>
  <div class="credit-limit-manager">
    <validation-observer ref="creditLimitRules">
      <b-form>
        <b-row class="d-flex align-items-center">
          <b-col
            md="6"
            class="mt-2 mb-2"
          >
            <InputNumberField
              id="creditLimit"
              v-model="localCreditLimitData.creditLimit"
              :label="creditLimitLabel"
              :placeholder="creditLimitPlaceholder"
              type="number"
              error-name="Credit Limit"
              rules="required"
              :readonly="localIsReadonly"
              @input="handleInputChange"
            />
          </b-col>
          <b-col
            v-if="!localIsReadonly"
            md="6"
          >
            <Button
              class="mt-2 mb-2"
              size="md"
              :label="buttonLabel"
              @click="handleCreditLimitAction"
            />
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>

    <!-- Credit Limit Table Section -->
    <b-table
      ref="creditLimitTable"
      :busy="isTableBusy"
      :per-page="localPerPage"
      :current-page="currentPage"
      :items="tableItems"
      responsive
      :fields="tableFields"
      primary-key="id"
      show-empty
      empty-text="No records available"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(approvedLimit)="data">
        {{ data.value ? toBalance(data.value) : '' }}
      </template>
      <template #cell(status)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
    </b-table>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import {
  BRow, BCol, BTable, BSpinner, BBadge, BForm,
} from 'bootstrap-vue'
import { tableMixins } from '@core/mixins/table'
import getStatusColor from '../../../helpers/getStatusColor'
import { toBalance } from '../../../helpers/formatAmount'
import InputNumberField from '../InputNumberField.vue'
import Button from '../../components/button/Button.vue'

export default {
  name: 'CreditLimit',
  components: {
    InputNumberField,
    Button,
    BRow,
    BCol,
    BTable,
    BSpinner,
    BBadge,
    BForm,
    ValidationObserver,
  },
  mixins: [tableMixins],
  props: {
    // Form props
    isReadonly: {
      type: Boolean,
      default: false,
    },
    creditLimitData: {
      type: Object,
      default: () => ({ creditLimit: null }),
    },
    creditLimitLabel: {
      type: String,
      default: 'Credit Limit',
    },
    creditLimitPlaceholder: {
      type: String,
      default: 'Enter credit limit',
    },
    buttonLabel: {
      type: String,
      default: 'Submit',
    },
    // Table props
    creditLimitItems: {
      type: Array,
      default: () => [],
    },
    isTableBusy: {
      type: Boolean,
      default: false,
    },
    customFields: {
      type: Array,
      default: () => [],
    },
    perPage: {
      type: Number,
      default: 10,
    },
    currentPageProp: {
      type: Number,
      default: 1,
    },
    sortByProp: {
      type: String,
      default: null,
    },
    sortDescProp: {
      type: Boolean,
      default: false,
    },
    initialFormData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      getStatusColor,
      toBalance,
      localCreditLimitData: {
        creditLimit: this.creditLimitData.creditLimit || null,
      },
      currentPage: this.currentPageProp,
      sortBy: this.sortByProp,
      sortDesc: this.sortDescProp,
      tableItems: [],
      localPerPage: this.perPage,
      localIsReadonly: this.isReadonly,
      defaultFields: [
        { key: 'SN', label: 'S/N' },
        { key: 'partnerCode', sortable: true, label: 'Partner Code' },
        { key: 'partnerName', sortable: true, label: 'Partner Name' },
        { key: 'approvedLimit', sortable: true, label: 'Approved Limit' },
        { key: 'status', sortable: true, label: 'Status' },
      ],
    }
  },
  computed: {
    // Combine default fields with any custom fields passed as props
    tableFields() {
      return [...this.defaultFields, ...this.customFields]
    },

    // Calculate total rows for pagination
    totalRows() {
      return this.tableItems.length
    },
  },

  watch: {
    // Keep local data in sync with props
    creditLimitData: {
      handler(newVal) {
        if (newVal && newVal.creditLimit !== undefined) {
          this.localCreditLimitData = { ...newVal }
        }
      },
      deep: true,
      immediate: true,
    },
    creditLimitItems: {
      handler(newVal) {
        if (Array.isArray(newVal)) {
          this.tableItems = [...newVal]
        }
      },
      deep: true,
      immediate: true,
    },
    currentPageProp(newVal) {
      this.currentPage = newVal
    },
    sortByProp(newVal) {
      this.sortBy = newVal
    },
    sortDescProp(newVal) {
      this.sortDesc = newVal
    },
    perPage(newVal) {
      this.localPerPage = newVal
    },
    isReadonly(newVal) {
      this.localIsReadonly = newVal
    },
    isEdit: {
      immediate: true,
      handler(val) {
        this.localIsReadonly = val
      },
    },
    initialFormData: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue && Object.keys(newValue).length) {
          const newData = {
            partnerCode: newValue.partnerCode,
            partnerName: newValue.institutionName,
            approvedLimit: newValue.creditLimit
              ? toBalance(newValue.creditLimit)
              : newValue.creditLimit,
          }
          this.tableItems = [newData]

          // If credit limit is included, update the input field
          if (newValue.creditLimit !== undefined) {
            this.localCreditLimitData = {
              creditLimit: newValue.creditLimit
                ? toBalance(newValue.creditLimit)
                : newValue.creditLimit,
            }
          }
        }
      },
    },
  },

  created() {
    // Initialize table data from creditLimitItems prop
    if (Array.isArray(this.creditLimitItems) && this.creditLimitItems.length > 0) {
      this.tableItems = [...this.creditLimitItems]
    }
  },

  methods: {
    // Handle input changes immediately
    handleInputChange(value) {
      this.$emit('input-change', {
        ...this.localCreditLimitData,
        creditLimit: value,
      })
    },

    // Handle form submission with validation
    handleCreditLimitAction() {
      this.$refs.creditLimitRules.validate().then(success => {
        if (success) {
          this.$emit('credit-limit-action', {
            ...this.localCreditLimitData,
            actionType: this.buttonLabel,
          })
        }
      })
    },

    // Emit form updates
    emitFormUpdate() {
      this.$emit('update:credit-limit-data', this.localCreditLimitData)
    },

    // Validate the form manually if needed
    validate() {
      return this.$refs.creditLimitRules.validate()
    },

    // Reset the form
    reset() {
      this.localCreditLimitData = { creditLimit: null }
      this.$refs.creditLimitRules.reset()
    },

    // Update the component with new data from parent
    updateData(newData) {
      if (newData) {
        if (newData.creditLimit !== undefined) {
          this.localCreditLimitData.creditLimit = newData.creditLimit
        }

        const newValues = {
          partnerCode: newData.partnerCode,
          partnerName: newData.institutionName,
          approvedLimit: newData.creditLimit
            ? toBalance(newData.creditLimit)
            : newData.creditLimit,
        }
        this.tableItems = [newValues]
      }
    },
  },
}
</script>

<style>
.nav-tabs .nav-link:after {
    background:  var(--primary) !important;
  }
</style>
